/* global Word */

import { safeAlert, reportResult, paraDetail, RunDetail } from '../runs/common';
import { ensureRunStyles } from '../taskpane/styleEngine';
import { getConfig, AppendixConfig } from '../taskpane/configService';

const W_NS   = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";
const W14_NS = "http://schemas.microsoft.com/office/word/2010/wordml";

/** Convert a live DOM collection into a typed array, dropping any null slots. */
function toArray<T extends Element>(
  collection: HTMLCollectionOf<Element> | NodeListOf<Element>
): T[] {
  const result: T[] = [];
  for (let i = 0; i < collection.length; i++) {
    const item = collection.item(i);
    if (item) result.push(item as T);
  }
  return result;
}

/** All <w:NAME> descendants of `el` as a typed array (no null slots). */
function childElements(el: Element, name: string): Element[] {
  return toArray(el.getElementsByTagNameNS(W_NS, name));
}

/** Concatenated <w:t> text content of an element, trimmed. */
function elementText(el: Element): string {
  let txt = "";
  for (const t of childElements(el, "t")) txt += t.textContent || "";
  return txt.trim();
}

/**
 * Normalize a string for fuzzy text matching: collapse runs of any
 * whitespace (regular space, tab, NBSP, newline) to a single space and
 * trim. Used to compare pendingNavs (built from OOXML <w:t> extraction)
 * against Word's paragraph.text — the two sources represent tabs and
 * other inline non-text elements differently, so a strict equality
 * check fails in those cases.
 */
function normalizeForMatch(s: string): string {
  return (s || "").replace(/[\s\u00A0]+/g, " ").trim();
}

/**
 * Extract the visible text content of a paragraph's OOXML.
 *
 *   • <w:t>...</w:t>  → captured verbatim
 *   • <w:tab/>        → emitted as a single space (matches what Word's
 *                       .text effectively becomes after normalization)
 *
 * The result feeds the post-insertOoxml re-location pass, where we
 * compare it against paragraph.text via normalizeForMatch().
 */
function extractParaText(paraOoxml: string): string {
  // Two alternatives in one regex: a <w:t>…</w:t> element OR a <w:tab…/>
  // (self-closing, possibly with attributes). For tab matches m[1] is
  // undefined, so we emit a space; the comparison side normalizes anyway.
  const re = /<w:t[^>]*>([^<]*)<\/w:t>|<w:tab\b[^>]*\/?>/g;
  let text = "";
  let m = re.exec(paraOoxml);
  while (m !== null) {
    text += m[1] === undefined ? " " : m[1];
    m = re.exec(paraOoxml);
  }
  return text.trim();
}

// ─────────────────────────────────────────────────────────────────────────────
// excludeHyperlinkData — all paraIds stored UPPERCASE
// ─────────────────────────────────────────────────────────────────────────────

/** Collect TOC/bookmark hyperlink anchors in one paragraph; updates the
 *  exclude list + anchor map. */
function collectParaHyperlinks(
  para: Element,
  paraId: string,
  excludeHypTocData: string[],
  hypAncData: Record<string, string>
): void {
  for (const hyp of childElements(para, "hyperlink")) {
    const anchor = hyp.getAttributeNS(W_NS, "anchor") || "";
    const tTags  = hyp.getElementsByTagNameNS(W_NS, "t");
    if (anchor && (anchor.startsWith("_Toc") || /^_bookmark\d+/.test(anchor)) && tTags.length > 0) {
      if (excludeHypTocData.indexOf(paraId) === -1) excludeHypTocData.push(paraId);
      hypAncData[paraId] = anchor;
    }
  }
}

function excludeHyperlinkData(xmlDoc: Document): {
  excludeHypTocData: string[];
  hypAncData: Record<string, string>;
} {
  const excludeHypTocData: string[] = [];
  const hypAncData: Record<string, string> = {};
  const paras = toArray(xmlDoc.getElementsByTagNameNS(W_NS, "p"));

  for (const para of paras) {
    const paraId = (para.getAttributeNS(W14_NS, "paraId") || "").toUpperCase();
    if (!paraId) continue;

    const pStyle = childElements(para, "pStyle")[0];
    if (pStyle) {
      const val = pStyle.getAttributeNS(W_NS, "val") || "";
      if (val.startsWith("TO")) excludeHypTocData.push(paraId);
    }

    collectParaHyperlinks(para, paraId, excludeHypTocData, hypAncData);
  }

  return { excludeHypTocData, hypAncData };
}

// ─────────────────────────────────────────────────────────────────────────────
// getAppendixParas — all paraIds stored UPPERCASE
// ─────────────────────────────────────────────────────────────────────────────

/** Reasons an "Appendix …" paragraph is NOT a real appendix heading. */
function isExcludedAppendixText(txt: string, paraId: string, excludeHypTocData: string[]): string | null {
  if (!paraId) return "no paraId";
  if (excludeHypTocData.indexOf(paraId) !== -1) return "in TOC/hyperlink exclude list";
  if (txt.indexOf("...") !== -1) return 'contains "..." (TOC dot leader)';
  if (txt.trim().toLowerCase() === "appendix") return "bare 'Appendix' with no title";
  const normalized = txt.replace(/[\s\u00A0]+/g, "");
  if (/^Appendix[A-Z](?:-?\d+|Table\d+|Figure\d+)/.test(normalized)) {
    return `cross-ref pattern (${normalized.substring(0, 30)})`;
  }
  return null;
}

/** True if the previous paragraph is a Heading (other than "Appendices"),
 *  which disqualifies the current "Appendix …" line from being a heading. */
function prevParaIsDisqualifyingHeading(previousPara: Element | null): boolean {
  if (previousPara === null) return false;
  const pTxt = elementText(previousPara).toLowerCase();
  const prevPStyle = childElements(previousPara, "pStyle")[0];
  if (!prevPStyle) return false;
  const prevStyle = prevPStyle.getAttributeNS(W_NS, "val") || "";
  console.log(`         prev   : "${pTxt.substring(0, 40)}" | pStyle: "${prevStyle}"`);
  return prevStyle.indexOf("Heading") !== -1 && pTxt !== "appendices" && pTxt !== "appendix";
}

/** If a confirmed _Toc bookmark (present in the TOC anchor set) starts in
 *  this paragraph, return its name; else null. */
function findConfirmedTocBookmark(para: Element, hypAncValues: string[]): string | null {
  const bkmarkNames: string[] = [];
  let confirmed: string | null = null;
  for (const bm of childElements(para, "bookmarkStart")) {
    const nm = bm.getAttributeNS(W_NS, "name") || "";
    bkmarkNames.push(nm);
    if (nm.startsWith("_Toc") && hypAncValues.indexOf(nm) !== -1) {
      confirmed = nm;
      break;
    }
  }
  console.log(`         bookmarks: [${bkmarkNames.join(", ") || "none"}]`);
  return confirmed;
}

/** True if `para` sits inside a multi-cell table row (so it's not a heading). */
function isInsideMultiCellRow(para: Element): boolean {
  let parent = para.parentElement;
  while (parent !== null) {
    const ln = parent.localName;
    if (ln === "tr") {
      const cells = parent.getElementsByTagNameNS(W_NS, "tc").length;
      console.log(`         in table row with ${cells} cell(s)`);
      return cells > 1;
    }
    if (ln === "tbl" || ln === "body") break;
    parent = parent.parentElement;
  }
  return false;
}

interface AppendixScanAcc {
  appendixList: string[];
  appendixListWithoutBm: string[];
}

/** Classify one "Appendix …" paragraph into the bookmarked or non-bookmarked
 *  list (or skip it). */
function classifyAppendixPara(
  para: Element,
  txt: string,
  paraId: string,
  previousPara: Element | null,
  ctx: { excludeHypTocData: string[]; hypAncValues: string[] },
  acc: AppendixScanAcc
): void {
  const excludeReason = isExcludedAppendixText(txt, paraId, ctx.excludeHypTocData);
  if (excludeReason) {
    console.log(`         SKIP   : ${excludeReason}`);
    return;
  }

  if (prevParaIsDisqualifyingHeading(previousPara)) {
    console.log(`         SKIP   : previous para is Heading`);
    return;
  }

  const confirmedBm = findConfirmedTocBookmark(para, ctx.hypAncValues);
  if (confirmedBm !== null) {
    acc.appendixList.push(paraId);
    console.log(`         ADDED  → appendixList (bookmark "${confirmedBm}" confirmed in TOC)`);
    return;
  }

  if (isInsideMultiCellRow(para)) {
    console.log(`         SKIP   : inside multi-cell table row`);
  } else {
    acc.appendixListWithoutBm.push(paraId);
    console.log(`         ADDED  → appendixListWithoutBm (no bookmark, not in table)`);
  }
}

function getAppendixParas(
  xmlDoc: Document,
  excludeHypTocData: string[],
  hypAncData: Record<string, string>
): string[] {
  const acc: AppendixScanAcc = { appendixList: [], appendixListWithoutBm: [] };
  const hypAncValues = Object.values(hypAncData);

  const paras = toArray(xmlDoc.getElementsByTagNameNS(W_NS, "p"));
  let previousPara: Element | null = null;

  console.log(`\n${"─".repeat(60)}`);
  console.log(`  getAppendixParas — scanning ${paras.length} paragraphs`);
  console.log(`${"─".repeat(60)}`);

  for (let i = 0; i < paras.length; i++) {
    const para = paras[i];
    const txt  = elementText(para);

    if (txt.startsWith("Appendix")) {
      const paraId = (para.getAttributeNS(W14_NS, "paraId") || "").toUpperCase();
      console.log(`\n  [P${i + 1}] TEXT   : "${txt.substring(0, 80)}"`);
      console.log(`         paraId : ${paraId || "⚠ MISSING"}`);
      classifyAppendixPara(para, txt, paraId, previousPara, { excludeHypTocData, hypAncValues }, acc);
    }

    previousPara = para;
  }

  console.log(`\n${"─".repeat(60)}`);
  console.log(`  appendixList         : ${acc.appendixList.length} → [${acc.appendixList.join(", ")}]`);
  console.log(`  appendixListWithoutBm: ${acc.appendixListWithoutBm.length} → [${acc.appendixListWithoutBm.join(", ")}]`);
  const chosen = acc.appendixList.length > 0 ? acc.appendixList : acc.appendixListWithoutBm;
  console.log(`  CHOSEN               : ${chosen.length} para(s)`);
  console.log(`${"─".repeat(60)}\n`);

  return chosen;
}

// ─────────────────────────────────────────────────────────────────────────────
// applyPStyleChange
// ─────────────────────────────────────────────────────────────────────────────

const PSTYLE_RE = /<w:pStyle\s+w:val="([^"]+)"/;

/** Case A: no <w:pPr> at all — insert a fresh pPr with the target style. */
function insertNewPPr(
  paraOoxml: string, targetStyle: string, trackId: number, qcAuthor: string, qcDate: string
): { ooxml: string; fixed: boolean; nextId: number } {
  console.log(`      pStyle: no <w:pPr> — INSERTING new pPr with pStyle="${targetStyle}"`);
  const pOpenTagEnd = paraOoxml.indexOf(">") + 1;
  const newPPr =
    `<w:pPr>` +
      `<w:pStyle w:val="${targetStyle}"/>` +
      `<w:pPrChange w:id="${trackId}" w:author="${qcAuthor}" w:date="${qcDate}">` +
        `<w:pPr/>` +
      `</w:pPrChange>` +
    `</w:pPr>`;
  console.log(`      pStyle: (no pPr) → "${targetStyle}" ✓ inserted (trackId ${trackId})`);
  return {
    ooxml  : paraOoxml.substring(0, pOpenTagEnd) + newPPr + paraOoxml.substring(pOpenTagEnd),
    fixed  : true,
    nextId : trackId + 1,
  };
}

function applyPStyleChange(
  paraOoxml: string,
  targetStyle: string,
  trackId: number,
  qcAuthor: string,
  qcDate: string
): { ooxml: string; fixed: boolean; nextId: number } {

  if (paraOoxml.indexOf("<w:pPrChange") !== -1) {
    console.log(`      pStyle: already has pPrChange — skipped`);
    return { ooxml: paraOoxml, fixed: false, nextId: trackId };
  }

  const pPrStart = paraOoxml.indexOf("<w:pPr");
  const pPrEnd   = paraOoxml.indexOf("</w:pPr>", pPrStart);

  // ── Case A: No <w:pPr> block at all ──
  if (pPrStart === -1 || pPrEnd === -1) {
    return insertNewPPr(paraOoxml, targetStyle, trackId, qcAuthor, qcDate);
  }

  const pPrBlock     = paraOoxml.substring(pPrStart, pPrEnd + "</w:pPr>".length);
  const pStyleMatch  = PSTYLE_RE.exec(pPrBlock);
  const currentStyle = pStyleMatch ? pStyleMatch[1] : null;

  console.log(`      pStyle: current="${currentStyle || "none"}" target="${targetStyle}"`);

  if (currentStyle === targetStyle) {
    console.log(`      pStyle: already correct — no change`);
    return { ooxml: paraOoxml, fixed: false, nextId: trackId };
  }

  const innerStart   = pPrBlock.indexOf(">") + 1;
  const pPrInnerXml  = pPrBlock.substring(innerStart, pPrBlock.length - "</w:pPr>".length);
  const pPrChangeXml =
    `<w:pPrChange w:id="${trackId}" w:author="${qcAuthor}" w:date="${qcDate}">` +
    `<w:pPr>${pPrInnerXml}</w:pPr>` +
    `</w:pPrChange>`;

  let newPPr: string;

  if (currentStyle === null) {
    // ── Case B: pPr exists but no pStyle element ──
    const openEnd = pPrBlock.indexOf(">") + 1;
    newPPr =
      pPrBlock.substring(0, openEnd) +
      `<w:pStyle w:val="${targetStyle}"/>` +
      pPrBlock.substring(openEnd).replace("</w:pPr>", `${pPrChangeXml}</w:pPr>`);
  } else {
    // ── Case C: pStyle exists but wrong value ──
    newPPr = pPrBlock
      .replace(/<w:pStyle\s+w:val="[^"]+"/, `<w:pStyle w:val="${targetStyle}"`)
      .replace("</w:pPr>", `${pPrChangeXml}</w:pPr>`);
  }

  console.log(`      pStyle: "${currentStyle || "none"}" → "${targetStyle}" ✓ (trackId ${trackId})`);

  return {
    ooxml  : paraOoxml.substring(0, pPrStart) + newPPr + paraOoxml.substring(pPrStart + pPrBlock.length),
    fixed  : true,
    nextId : trackId + 1,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// applyRStyleChange
// ─────────────────────────────────────────────────────────────────────────────

const RSTYLE_RE   = /<w:rStyle\s+w:val="([^"]+)"/;
const RUN_TEXT_RE = /<w:t[^>]*>([^<]*)<\/w:t>/;

interface RStyleRunResult {
  /** Replacement <w:r> block (only when changed). */
  newRBlock: string;
  changed: boolean;
}

/** Apply the target rStyle to a single <w:r> block (adding an rPrChange).
 *  Returns the original block unchanged when no edit is needed. */
function applyRStyleToRun(
  rBlock: string, runIndex: number, targetStyle: string, trackId: number, qcAuthor: string, qcDate: string
): RStyleRunResult {
  const runTextMatch = RUN_TEXT_RE.exec(rBlock);
  const runText      = runTextMatch ? runTextMatch[1].substring(0, 30) : "(no text)";

  const rPrStart = rBlock.indexOf("<w:rPr>");
  const rPrEnd   = rBlock.indexOf("</w:rPr>");
  if (rPrStart === -1 || rPrEnd === -1) {
    console.log(`        [R${runIndex}] "${runText}" — no rPr, skipped`);
    return { newRBlock: rBlock, changed: false };
  }

  const rPrBlock = rBlock.substring(rPrStart, rPrEnd + "</w:rPr>".length);

  if (rPrBlock.indexOf("<w:rPrChange") !== -1) {
    console.log(`        [R${runIndex}] "${runText}" — already has rPrChange, skipped`);
    return { newRBlock: rBlock, changed: false };
  }

  const rStyleMatch = RSTYLE_RE.exec(rPrBlock);
  if (!rStyleMatch) {
    console.log(`        [R${runIndex}] "${runText}" — no rStyle, skipped`);
    return { newRBlock: rBlock, changed: false };
  }

  const currentRStyle = rStyleMatch[1];
  if (currentRStyle === targetStyle) {
    console.log(`        [R${runIndex}] "${runText}" — rStyle already "${targetStyle}"`);
    return { newRBlock: rBlock, changed: false };
  }

  console.log(`        [R${runIndex}] "${runText}" — rStyle "${currentRStyle}" → "${targetStyle}" ✓`);

  const rPrInnerXml  = rPrBlock.replace("<w:rPr>", "").replace("</w:rPr>", "");
  const rPrChangeXml =
    `<w:rPrChange w:id="${trackId}" w:author="${qcAuthor}" w:date="${qcDate}">` +
    `<w:rPr>${rPrInnerXml}</w:rPr>` +
    `</w:rPrChange>`;

  const newRPrBlock = rPrBlock
    .replace(/<w:rStyle\s+w:val="[^"]+"/, `<w:rStyle w:val="${targetStyle}"`)
    .replace("</w:rPr>", `${rPrChangeXml}</w:rPr>`);

  const newRBlock = rBlock.substring(0, rPrStart) + newRPrBlock + rBlock.substring(rPrStart + rPrBlock.length);
  return { newRBlock, changed: true };
}

function applyRStyleChange(
  paraOoxml: string,
  targetStyle: string,
  trackId: number,
  qcAuthor: string,
  qcDate: string
): { ooxml: string; fixed: number; nextId: number } {

  let modifiedOoxml = paraOoxml;
  let fixCount      = 0;
  let searchFrom    = 0;
  let runIndex      = 0;
  let nextId        = trackId;

  let rStart = modifiedOoxml.indexOf("<w:r>", searchFrom);
  while (rStart !== -1) {
    const rEnd = modifiedOoxml.indexOf("</w:r>", rStart);
    if (rEnd === -1) break;

    runIndex++;
    const rBlock = modifiedOoxml.substring(rStart, rEnd + "</w:r>".length);
    const res = applyRStyleToRun(rBlock, runIndex, targetStyle, nextId, qcAuthor, qcDate);

    if (res.changed) {
      modifiedOoxml = modifiedOoxml.substring(0, rStart) + res.newRBlock + modifiedOoxml.substring(rStart + rBlock.length);
      fixCount++;
      nextId++;
      searchFrom = rStart + res.newRBlock.length;
    } else {
      searchFrom = rStart + 1;
    }

    rStart = modifiedOoxml.indexOf("<w:r>", searchFrom);
  }

  if (fixCount === 0) console.log(`        no rStyle changes needed (${runIndex} run(s) checked)`);
  else console.log(`        ${fixCount} run(s) updated ✓`);

  return { ooxml: modifiedOoxml, fixed: fixCount, nextId };
}

// ─────────────────────────────────────────────────────────────────────────────
// run16 — main entry point
// ─────────────────────────────────────────────────────────────────────────────

const PARA_TEXT_RE = /<w:t[^>]*>([^<]+)<\/w:t>/;

interface Run16Acc {
  modifiedBodyOoxml: string;
  paraStyleFixed: number;
  charStyleFixed: number;
  trackId: number;
  pendingNavs: { text: string }[];
}

/** Apply pStyle + rStyle fixes to one appendix paragraph (matched by paraId)
 *  within the running body OOXML. Mutates `acc`. */
function processAppendixPara(paraId: string, pid: number, c: AppendixConfig, acc: Run16Acc): void {
  const paraRegex = new RegExp(
    String.raw`<w:p\b[^>]*w14:paraId="${paraId}"[^>]*>[\s\S]*?</w:p>`,
    "i"
  );

  const paraMatch = paraRegex.exec(acc.modifiedBodyOoxml);
  if (!paraMatch) {
    console.log(`\n  ⚠ [${pid + 1}] paraId "${paraId}" NOT FOUND in body OOXML`);
    const debugMatch = /w14:paraId="([^"]+)"[^>]*>[\s\S]{0,200}Appendix/i.exec(acc.modifiedBodyOoxml);
    if (debugMatch) console.log(`        Nearby paraId example: "${debugMatch[1]}"`);
    return;
  }

  const originalParaOoxml = paraMatch[0];
  const textMatch = PARA_TEXT_RE.exec(originalParaOoxml);
  const paraText  = textMatch ? textMatch[1].substring(0, 60) : "(no text)";

  console.log(`\n  [${pid + 1}] ✓ FOUND "${paraText}" | paraId: ${paraId}`);

  let modifiedParaOoxml = originalParaOoxml;
  let paraChanged       = false;

  // ── pStyle: Cases A / B / C ──
  const pResult = applyPStyleChange(modifiedParaOoxml, c.appendixStyle, acc.trackId, c.qcAuthor, c.qcDate);
  if (pResult.fixed) {
    modifiedParaOoxml = pResult.ooxml;
    acc.trackId       = pResult.nextId;
    acc.paraStyleFixed++;
    paraChanged = true;
  }

  // ── rStyle: Case C only ──
  const rResult = applyRStyleChange(modifiedParaOoxml, c.appendixStyle, acc.trackId, c.qcAuthor, c.qcDate);
  if (rResult.fixed > 0) {
    modifiedParaOoxml = rResult.ooxml;
    acc.trackId       = rResult.nextId;
    acc.charStyleFixed += rResult.fixed;
    paraChanged        = true;
  }

  if (paraChanged) {
    acc.modifiedBodyOoxml = acc.modifiedBodyOoxml.replace(originalParaOoxml, modifiedParaOoxml);
    const fullText = extractParaText(originalParaOoxml);
    if (fullText) acc.pendingNavs.push({ text: fullText });
    console.log(`      → paragraph updated in body OOXML ✓`);
  } else {
    console.log(`      → already correctly styled, no changes needed`);
  }
}

/** First not-yet-consumed paragraph satisfying `pred`, or null. */
function firstUnconsumed(
  bodyParas: Word.ParagraphCollection,
  consumed: Set<string>,
  pred: (bp: Word.Paragraph) => boolean
): Word.Paragraph | null {
  for (const bp of bodyParas.items) {
    if (consumed.has(bp.uniqueLocalId)) continue;
    if (pred(bp)) return bp;
  }
  return null;
}

/** Find a freshly written appendix paragraph by normalized text, preferring
 *  the strongest signal first (see strategy comment). Returns null if none. */
function relocateNavParagraph(
  needle: string,
  bodyParas: Word.ParagraphCollection,
  appendixStyle: string,
  consumed: Set<string>
): { para: Word.Paragraph; strategy: string } | null {
  // Strategy 1: exact normalized + correct style (strongest — we just applied it).
  const exactStyled = firstUnconsumed(bodyParas, consumed,
    bp => normalizeForMatch(bp.text) === needle && bp.style === appendixStyle);
  if (exactStyled) return { para: exactStyled, strategy: "exact+style" };

  // Strategy 2: prefix (≥10 chars) + correct style (handles tail drift).
  if (needle.length >= 10) {
    const prefix = needle.substring(0, Math.min(60, needle.length));
    const prefixStyled = firstUnconsumed(bodyParas, consumed,
      bp => bp.style === appendixStyle && normalizeForMatch(bp.text).startsWith(prefix));
    if (prefixStyled) return { para: prefixStyled, strategy: "prefix+style" };
  }

  // Strategy 3: exact normalized, any style — last-resort fallback.
  const exactAny = firstUnconsumed(bodyParas, consumed,
    bp => normalizeForMatch(bp.text) === needle);
  if (exactAny) return { para: exactAny, strategy: "exact (style mismatch)" };

  return null;
}

/** Re-locate every modified appendix paragraph in the freshly written body
 *  and build Go To nav rows (insertOoxml("Replace") invalidated old ids). */
async function captureNavRows(
  context: Word.RequestContext,
  pendingNavs: { text: string }[],
  appendixStyle: string,
  navDetails: RunDetail[]
): Promise<void> {
  try {
    const bodyParas = context.document.body.paragraphs;
    bodyParas.load("items/text, items/uniqueLocalId, items/style");
    await context.sync();

    console.log(`\n  Re-locating ${pendingNavs.length} modified paragraph(s) ` +
                `among ${bodyParas.items.length} body paragraph(s)...`);

    const consumed = new Set<string>();  // uniqueLocalIds already matched

    for (const nav of pendingNavs) {
      const needle = normalizeForMatch(nav.text);
      if (!needle) {
        console.log(`  (skipped: empty needle for "${nav.text.substring(0, 40)}")`);
        continue;
      }

      const hit = relocateNavParagraph(needle, bodyParas, appendixStyle, consumed);
      if (hit) {
        consumed.add(hit.para.uniqueLocalId);
        navDetails.push(paraDetail(hit.para.uniqueLocalId, nav.text.trim(), `→ ${appendixStyle}`));
        console.log(`  ✓ Go To row [${hit.strategy}] "${nav.text.substring(0, 40)}" → paraId=${hit.para.uniqueLocalId}`);
      } else {
        console.log(`  ⚠ Could not re-locate "${nav.text.substring(0, 40)}" — skipping nav row`);
      }
    }

    console.log(`  Nav rows produced: ${navDetails.length} / ${pendingNavs.length}`);
  } catch (navErr) {
    console.log(`  ⚠ Nav row capture failed: ${(navErr as Error).message}`);
  }
}

function reportRun16Results(c: AppendixConfig, appendixCount: number, acc: Run16Acc, navDetails: RunDetail[]): void {
  const totalFixes = acc.paraStyleFixed + acc.charStyleFixed;

  console.log("\n" + "═".repeat(70));
  console.log(`Appendix paragraphs found : ${appendixCount}`);
  console.log(`Paragraph styles fixed    : ${acc.paraStyleFixed}`);
  console.log(`Character styles fixed    : ${acc.charStyleFixed}`);
  console.log(`Total fixes               : ${totalFixes}`);
  console.log(`Go To rows in summary     : ${navDetails.length}`);
  console.log("✓ Style Appendix check complete!");
  console.log("═".repeat(70));

  if (totalFixes > 0) {
    reportResult('Appendix Styling Complete', [
      { label: 'Appendix paragraphs found', count: appendixCount },
      { label: `Paragraph style → ${c.appendixStyle}`, count: acc.paraStyleFixed },
      { label: `Character style → ${c.appendixStyle}`, count: acc.charStyleFixed },
      ...navDetails,
    ]);
  } else {
    // Appendix paragraphs exist but all are already correctly styled.
    reportResult('Appendix Styling — no changes needed', [
      { label: 'Appendix paragraphs found', count: appendixCount, note: 'all already correctly styled' },
      { label: 'Paragraph styles fixed', count: 0 },
      { label: 'Character styles fixed', count: 0 },
    ]);
  }
}

export async function run16(): Promise<"Success" | ["Failure", string]> {
  // ── Read config ──
  const cfg = getConfig();
  const c: AppendixConfig = cfg.run16_appendix;

  try {
    await Word.run(async (context) => {
      await ensureRunStyles(context, 16);

      console.log("═".repeat(70));
      console.log("RUN 16: Style Appendix Check");
      console.log("═".repeat(70));

      const body            = context.document.body;
      const bodyOoxmlResult = body.getOoxml();
      await context.sync();

      const rawBodyOoxml = bodyOoxmlResult.value;
      const xmlDoc       = new DOMParser().parseFromString(rawBodyOoxml, "application/xml");

      const { excludeHypTocData, hypAncData } = excludeHyperlinkData(xmlDoc);
      console.log(`Excluded (TOC/hyperlink) paragraphs : ${excludeHypTocData.length}`);

      const appendixParaIds = getAppendixParas(xmlDoc, excludeHypTocData, hypAncData);
      console.log(`Appendix paragraphs identified      : ${appendixParaIds.length}`);

      if (appendixParaIds.length === 0) {
        // No appendix heading found. Still emit a summary card.
        reportResult('Appendix Styling — no changes needed', [
          { label: 'No appendix paragraphs found', note: 'No changes made' },
          { label: 'Paragraph styles fixed', count: 0 },
          { label: 'Character styles fixed', count: 0 },
        ]);
        return;
      }

      const acc: Run16Acc = {
        modifiedBodyOoxml: rawBodyOoxml,
        paraStyleFixed: 0,
        charStyleFixed: 0,
        trackId: c.trackIdStart,
        pendingNavs: [],
      };

      console.log(`\n${"─".repeat(60)}`);
      console.log(`  Modifying body OOXML — ${appendixParaIds.length} paragraph(s) to process`);
      console.log(`${"─".repeat(60)}`);

      for (let pid = 0; pid < appendixParaIds.length; pid++) {
        processAppendixPara(appendixParaIds[pid], pid, c, acc);
      }

      const totalFixes = acc.paraStyleFixed + acc.charStyleFixed;
      const navDetails: RunDetail[] = [];

      if (totalFixes > 0) {
        console.log(`\n${"─".repeat(60)}`);
        console.log(`  Writing modified body OOXML back to document...`);

        context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
        await context.sync();

        body.insertOoxml(acc.modifiedBodyOoxml, "Replace");
        await context.sync();

        console.log(`  → body.insertOoxml written ✓`);
        console.log(`${"─".repeat(60)}`);

        if (acc.pendingNavs.length > 0) {
          await captureNavRows(context, acc.pendingNavs, c.appendixStyle, navDetails);
        }
      } else {
        console.log(`\n  Nothing to write — all paragraphs already correctly styled`);
      }

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      reportRun16Results(c, appendixParaIds.length, acc, navDetails);
    });

    return "Success";

  } catch (error) {
    console.error("run16 error:", error);
    safeAlert("Error in Appendix Style check:\n" + (error as Error).message);
    return ["Failure", (error as Error).message];
  }
}